import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  ButtonGroup,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { copyText, Show } from '@pelicargo/ui';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { trpc } from '../../config/trpc';
import { useApiKeys } from '../../hooks/apiKey/useApiKeys';
import { SimpleCheckbox } from '../SimpleCheckbox';
import { SimpleForm } from '../SimpleForm';
import { SimpleInput } from '../SimpleInput';
import { SimpleReactSelect } from '../SimpleReactSelect';

const schema = z.object({
  description: z.string().trim().min(1, 'Enter a description'),
  default_user_id: z.number({ required_error: 'Pick a default user', invalid_type_error: 'Pick a default user' }),
  acknowledged: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

type Props = {
  accountId: number;
  isOpen: boolean;
  onClose: () => void;
};

const DEFAULT_VALUES: FormValues = {
  description: '',
  default_user_id: undefined,
  acknowledged: false,
};

export const GenerateApiKeyModal = ({ accountId, isOpen, onClose }: Props) => {
  const toast = useToast();
  const methods = useForm<FormValues>({
    defaultValues: DEFAULT_VALUES,
    resolver: zodResolver(schema),
  });
  const { data: permissions } = trpc.getPermissions.useQuery(
    { accountId },
    { enabled: isOpen, refetchOnWindowFocus: false },
  );
  const { createApiKey, invalidateApiKeys, isCreating, resetCreateApiKey } = useApiKeys(accountId);
  const [secret, setSecret] = useState<string>();
  const [hasCopied, setHasCopied] = useState(false);

  const userOptions = useMemo(() => {
    const users = new Map<number, string>();

    for (const permission of permissions?.permissions ?? []) {
      if (!permission.user) continue;
      users.set(permission.user.id, permission.user.full_name || permission.user.email || `User ${permission.user.id}`);
    }

    return Array.from(users, ([value, label]) => ({ value, label }));
  }, [permissions]);

  useEffect(() => {
    if (isOpen) return;
    setSecret(undefined);
    setHasCopied(false);
    methods.reset(DEFAULT_VALUES);
    resetCreateApiKey();
  }, [isOpen, methods, resetCreateApiKey]);

  const clearAndClose = () => {
    setSecret(undefined);
    setHasCopied(false);
    methods.reset(DEFAULT_VALUES);
    resetCreateApiKey();
    onClose();
  };

  const handleModalClose = () => {
    if (secret) return;
    clearAndClose();
  };

  const handleGenerate = methods.handleSubmit(async (values) => {
    try {
      const result = await createApiKey({
        account_id: accountId,
        description: values.description,
        default_user_id: values.default_user_id,
      });

      setSecret(result.secret);
      resetCreateApiKey();
    } catch (error) {
      toast({
        title: 'API key could not be generated',
        description: error?.message,
        status: 'error',
      });
    }
  });

  const handleCopy = async () => {
    if (!secret) return;

    try {
      await copyText(secret);
      setHasCopied(true);
      toast({ title: 'API key copied', status: 'success' });
    } catch (error) {
      toast({
        title: 'API key could not be copied',
        description: error?.message,
        status: 'error',
      });
    }
  };

  const handleDone = async () => {
    if (!methods.getValues('acknowledged')) {
      methods.setError('acknowledged', {
        type: 'manual',
        message: 'Confirm you have stored the key first',
      });
      return;
    }

    await invalidateApiKeys();
    clearAndClose();
  };

  const isRevealingSecret = !!secret;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleModalClose}
      closeOnEsc={!isRevealingSecret}
      closeOnOverlayClick={!isRevealingSecret}
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <SimpleForm {...methods} submitOnBlur={false}>
          <Show if={!isRevealingSecret}>
            <ModalCloseButton />
          </Show>
          <ModalHeader>{isRevealingSecret ? 'Copy your new key' : 'Generate API key'}</ModalHeader>
          <ModalBody>
            <Show if={!isRevealingSecret}>
              <Stack spacing="4">
                <SimpleInput
                  name="description"
                  label="Description"
                  placeholder="Production · TMS integration"
                  isRequired
                />
                <SimpleReactSelect
                  name="default_user_id"
                  label="Default user"
                  helper="Requests created with this key are authored by this user."
                  options={userOptions}
                  isRequired
                  isSearchable
                />
              </Stack>
            </Show>
            <Show if={isRevealingSecret}>
              <Stack spacing="4">
                <Alert status="warning" borderRadius="md">
                  <AlertIcon />
                  <AlertDescription>
                    This is the only time the key is shown. Store it somewhere safe before closing.
                  </AlertDescription>
                </Alert>
                <Stack spacing="1">
                  <Text fontSize="xs">API key</Text>
                  <InputGroup>
                    <Input value={secret ?? ''} readOnly fontFamily="monospace" pr="20" />
                    <InputRightElement width="20">
                      <Button size="sm" variant="ghost" onClick={handleCopy}>
                        {hasCopied ? 'Copied' : 'Copy'}
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                </Stack>
                <SimpleCheckbox name="acknowledged" label="I've copied and stored this key" ignoreOnBlur />
              </Stack>
            </Show>
          </ModalBody>
          <ModalFooter>
            <Show if={!isRevealingSecret}>
              <ButtonGroup>
                <Button variant="ghost" onClick={clearAndClose}>
                  Cancel
                </Button>
                <Button colorScheme="brand" onClick={handleGenerate} isLoading={isCreating}>
                  Generate
                </Button>
              </ButtonGroup>
            </Show>
            <Show if={isRevealingSecret}>
              <Button colorScheme="brand" onClick={handleDone}>
                Done
              </Button>
            </Show>
          </ModalFooter>
        </SimpleForm>
      </ModalContent>
    </Modal>
  );
};
